import { Table, TableBody, TableContainer } from '@mui/material';
import {
    TableCell,
    TableHeader,
    TableRow,
    TableSortLabel,
    TableHeaderCell
} from '@/components/shared/styles/Table';
import { FONT_WEIGHT } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { HistoryStatus } from '@/pages/history/HistoryStatus';
import { TipsHistory, TipsStatus } from '@/services/PaymentsServiceV2';
import { useNavigate } from 'react-router-dom';
import { UnderlinedLink } from '@/components/shared/UnderlinedLink';
import { useContext, useState } from 'react';
import { UserContext } from '@/contexts/user/UserContext';
import { useFormatting } from '@/utils/formatting';
import { DATE_FORMAT } from '@/lib/constants';
import { useOrdering } from '@/lib/hooks/useOrdering';
import { EditTipsDialog } from './EditTipsDialog';
import { EditIcon } from '@/icons/EditIcon';
import { CheckIcon } from '@/icons/CheckIcon';
import { WarningIcon } from '@/icons/WarningIcon';
import { ErrorIcon } from '@/icons/ErrorIcon';

export type TipsLocationListProps = {
    items?: TipsHistory[];
    reloadTransactions: () => void;
};

export const TipsLocationList = (props: TipsLocationListProps) => {
    const { t } = useTranslation();

    const { getBalanceLabel, toDecimal } = useFormatting();

    const { company } = useContext(UserContext);

    //const { company } = useContext(UserContext);
    const navigation = useNavigate();

    const { orderedItems, handleRequestSort, orderMap } = useOrdering(
        props?.items || [],
        'date',
        'desc'
    );

    const [editTip, setEditTip] = useState<TipsHistory | null>(null);

    const getReportingStatusIcon = (tip: TipsHistory) => {
        if (
            tip.reportingStatus === 'Reported' ||
            tip.reportingStatus === 'FullyReported'
        ) {
            return <CheckIcon />;
        }
        if (
            (tip.reportingStatus === 'PartiallyReported' ||
                tip.reportingStatus === 'NotReported') &&
            tip.amountSent > 0 &&
            !tip.isInCurrentPayrollPeriod
        ) {
            return <ErrorIcon />;
        }

        return null;
    };

    const getAmountReportedColor = (tip: TipsHistory) => {
        if (
            tip.isInCurrentPayrollPeriod &&
            tip.reportingStatus === 'NotReported' &&
            tip.amountSent === 0
        ) {
            return 'inherit';
        }
        return (tip.reportingStatus === 'NotReported' ||
            tip.reportingStatus === 'PartiallyReported') &&
            tip.amountSent > 0
            ? '#D92D20'
            : 'inherit';
    };

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell
                                sx={{ fontWeight: 'bold', width: '180px' }}
                            >
                                <TableSortLabel
                                    direction={orderMap.date || 'asc'}
                                    onClick={() => handleRequestSort('date')}
                                >
                                    {t('Date')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell
                                sx={{ fontWeight: 'bold', width: '180px' }}
                            >
                                <TableSortLabel
                                    direction={
                                        orderMap.lastDistributedAt || 'asc'
                                    }
                                    onClick={() =>
                                        handleRequestSort('lastDistributedAt')
                                    }
                                >
                                    {t('Finalized Date')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell
                                sx={{ fontWeight: 'bold', width: '200px' }}
                            >
                                {t('Location')}
                            </TableHeaderCell>

                            <TableHeaderCell sx={{ fontWeight: 'bold' }}>
                                {t('Status')}
                            </TableHeaderCell>
                            <TableHeaderCell
                                align="right"
                                sx={{ fontWeight: 'bold' }}
                            >
                                <TableSortLabel
                                    align="right"
                                    direction={
                                        orderMap.totalEmployeeHours || 'asc'
                                    }
                                    onClick={() =>
                                        handleRequestSort('totalEmployeeHours')
                                    }
                                >
                                    {t('Approved hours')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell
                                align="right"
                                sx={{ fontWeight: 'bold' }}
                            >
                                <TableSortLabel
                                    align="right"
                                    direction={orderMap.totalTips || 'asc'}
                                    onClick={() =>
                                        handleRequestSort('totalTips')
                                    }
                                >
                                    {t('Total Tips')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell
                                align="center"
                                sx={{ fontWeight: 'bold' }}
                            >
                                {t('Reported')}
                            </TableHeaderCell>
                            <TableHeaderCell
                                align="right"
                                sx={{ fontWeight: 'bold', width: '150px' }}
                            >
                                {t('Amount reported')}
                            </TableHeaderCell>
                            <TableHeaderCell></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {orderedItems?.map((tip, index) => (
                            <TableRow
                                hoverEffect
                                key={JSON.stringify(tip) + index}
                                onClick={() => {
                                    navigation(
                                        `/tips-location/${tip.posJournalId}/page/0?status=${tip.status}`
                                    );
                                }}
                            >
                                <TableCell>
                                    <UnderlinedLink>
                                        {' '}
                                        {tip.date?.format(
                                            DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR
                                        )}
                                    </UnderlinedLink>
                                </TableCell>
                                <TableCell>
                                    {tip.lastDistributedAt
                                        ? tip.lastDistributedAt?.format(
                                              DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR
                                          )
                                        : '-'}
                                </TableCell>
                                <TableCell>{tip.locationName}</TableCell>
                                <TableCell
                                    sx={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                                    component="th"
                                    scope="row"
                                >
                                    <HistoryStatus
                                        showDot={true}
                                        status={tip.status}
                                    />
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                                >
                                    {toDecimal(tip.totalEmployeeHours, 2)}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                                >
                                    {getBalanceLabel(
                                        tip.totalTips,
                                        company?.currency
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {getReportingStatusIcon(tip)}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: FONT_WEIGHT.MEDIUM,
                                        color: getAmountReportedColor(tip)
                                    }}
                                >
                                    {tip.amountSent > 0
                                        ? getBalanceLabel(
                                              tip.amountSent,
                                              company?.currency
                                          )
                                        : ''}
                                </TableCell>
                                <TableCell
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditTip(tip);
                                    }}
                                >
                                    <EditIcon size={16} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <EditTipsDialog
                editTip={editTip}
                onClose={() => {
                    setEditTip(null);
                }}
            />
        </>
    );
};
